import { useState, useEffect } from "react";
import { API } from "../../../cache";
import { SideDrawerShell } from "../../common/components";
import RefEntityAutocomplete from "./RefEntityAutocomplete";
import "../../../DailyTimesheetView.css";
import "../../../V2SideDrawer.css";
import "../styles/TimesheetEntryDrawer.css";

const STATUS_OPTS = ["Draft", "Submitted", "Approved"];

function today() {
  return new Date().toISOString().slice(0, 10);
}

// Defined outside the drawer so React never treats it as a new component type
// on re-render (which would remount children and reset focus).
function DrwField({ label, req, children, err }) {
  return (
    <div className="dts-drw-field">
      <label className={`v2drw-form-label${req ? " dts-drw-label--req" : ""}`}>{label}</label>
      {children}
      {err && <div className="dts-drw-err">{err}</div>}
    </div>
  );
}

const EMPTY_FORM = {
  report_date:   "",
  ref_type:      "",
  ref_id:        "",
  title:         "",
  overall_notes: "",
  hours_spent:   0,
  minutes_spent: 0,
  status:        "Draft",
  epic_id:       "",
};

function validate(form, requiresDefectEpic) {
  const errors = {};
  if (!form.ref_id)       errors.ref     = "Select a reference entity";
  if (!form.title.trim()) errors.title   = "Required";
  const h = Number(form.hours_spent);
  const m = Number(form.minutes_spent);
  if (isNaN(h) || h < 0 || h > 8)  errors.hours   = "Must be 0–8";
  if (isNaN(m) || m < 0 || m > 59) errors.minutes = "Must be 0–59";
  // No timesheet entry against a defect is allowed without an epic — not
  // even Draft. Mirrors the backend's _apply_defect_epic_requirement, which
  // now rejects every status, not just Submit/Approve.
  if (requiresDefectEpic && !form.epic_id) errors.epic_id = "Pick an epic — required to log time against this defect";
  return errors;
}

export default function TimesheetEntryDrawer({
  open,
  entities,
  entitiesLoading,
  showToast,
  onClose,
  onSaved,
}) {
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Defect has no epic yet — needs one attached via this entry (see
  // _apply_defect_epic_requirement on the backend). Only relevant when
  // ref_type === "defect"; null once the defect already has its own epic.
  const [defectEpicOptions, setDefectEpicOptions] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM, report_date: today() });
      setErrors({});
      setDefectEpicOptions(null);
    }
  }, [open]);

  useEffect(() => {
    if (form.ref_type !== "defect" || !form.ref_id) {
      setDefectEpicOptions(null);
      return;
    }
    let cancelled = false;
    fetch(`${API}/defects/${form.ref_id}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(defect => {
        if (cancelled || !defect) return;
        if (defect.epic_id) {
          setDefectEpicOptions(null);
          return;
        }
        if (!defect.project_id) return;
        return fetch(`${API}/epics?project_id=${defect.project_id}&include_closed=false`, { credentials: "include" })
          .then(r => r.ok ? r.json() : [])
          .then(data => {
            if (cancelled) return;
            const list = Array.isArray(data) ? data : (data?.items || []);
            setDefectEpicOptions(list);
          });
      })
      .catch(() => { if (!cancelled) setDefectEpicOptions(null); });
    return () => { cancelled = true; };
  }, [form.ref_type, form.ref_id]);

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => {
      if (!e[field]) return e;
      const next = { ...e };
      delete next[field];
      return next;
    });
  };

  const handleSave = async () => {
    const errs = validate(form, !!defectEpicOptions);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      showToast("Please fill in the required fields", "warn");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        hours_spent:   Number(form.hours_spent),
        minutes_spent: Number(form.minutes_spent),
      };
      const r = await fetch(`${API}/daily-reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || "Failed to create report");
      showToast("Report created");
      if (data?.budget_warning) window.alert(data.budget_warning);
      onSaved?.();
      onClose();
    } catch (e) {
      showToast(e?.message || "Failed to create report", "warn");
    }
    setSaving(false);
  };

  const footer = (
    <>
      <div className="v2drw-foot-spacer" />
      <button
        type="button"
        className="v2drw-btn-cancel"
        onClick={onClose}
        disabled={saving}
      >
        Cancel
      </button>
      <button
        type="button"
        className="v2drw-btn-save"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? "Creating…" : "Create Entry"}
      </button>
    </>
  );

  return (
    <SideDrawerShell
      open={open}
      subtitle="TIMESHEET"
      title="New Entry"
      onClose={onClose}
      footer={footer}
      width="min(520px, 100vw)"
    >
      <div className="dts-drw-form">
        <DrwField label="Date" req>
          <input
            type="date"
            className="v2drw-control"
            value={form.report_date}
            onChange={e => set("report_date", e.target.value)}
          />
        </DrwField>

        <DrwField label="Reference Entity" req err={errors.ref}>
          <RefEntityAutocomplete
            refId={form.ref_id}
            refType={form.ref_type}
            entities={entities}
            loading={entitiesLoading}
            required
            onChange={(id, type) => {
              setForm(f => ({ ...f, ref_id: id, ref_type: type, epic_id: "" }));
              setErrors(e => {
                if (!e.ref) return e;
                const next = { ...e };
                delete next.ref;
                return next;
              });
            }}
          />
        </DrwField>

        {defectEpicOptions && (
          <DrwField label="Epic" req err={errors.epic_id}>
            <select
              className={`v2drw-control${errors.epic_id ? " err" : ""}`}
              value={form.epic_id}
              onChange={e => set("epic_id", e.target.value)}
            >
              <option value="">This defect has no epic — pick one to log time against it</option>
              {defectEpicOptions.map(ep => (
                <option key={ep.id} value={ep.id}>{ep.epic_name}</option>
              ))}
            </select>
          </DrwField>
        )}

        <DrwField label="Title" req err={errors.title}>
          <input
            type="text"
            className="v2drw-control"
            placeholder="e.g. Sprint 12 Day 3"
            value={form.title}
            onChange={e => set("title", e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") handleSave(); }}
            autoFocus
          />
        </DrwField>

        <DrwField label="Overall Notes">
          <textarea
            className="v2drw-control"
            placeholder="Summary of today's work…"
            value={form.overall_notes}
            onChange={e => set("overall_notes", e.target.value)}
          />
        </DrwField>

        <div className="dts-drw-row">
          <DrwField label="Hours Spent" err={errors.hours}>
            <input
              type="number"
              className="v2drw-control"
              min={0}
              max={8}
              value={form.hours_spent}
              onChange={e => set("hours_spent", e.target.value)}
            />
          </DrwField>
          <DrwField label="Minutes Spent" err={errors.minutes}>
            <input
              type="number"
              className="v2drw-control"
              min={0}
              max={59}
              value={form.minutes_spent}
              onChange={e => set("minutes_spent", e.target.value)}
            />
          </DrwField>
        </div>

        <DrwField label="Status">
          <select
            className="v2drw-control"
            value={form.status}
            onChange={e => set("status", e.target.value)}
          >
            {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </DrwField>
      </div>
    </SideDrawerShell>
  );
}
